// Fix I (2026-09-17 — incidente "229 registros sin subir"): cuando el token
// vence, el servidor responde 401 a todo sync. Antes la app mostraba
// "Sincronizando…" para siempre y el usuario no tenía forma de renovar la
// sesión sin arriesgar la cola. Este cartel persistente permite volver a
// iniciar sesión EN EL LUGAR, sin cerrar sesión y sin tocar ni una tabla.
import React, { useEffect, useState } from "react";
import { KeyRound, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { onSyncState, isSessionExpired, getPendingCount } from "@/lib/sync";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export function SessionExpiredBanner() {
  const { user, reauth } = useAuth();
  const [expired, setExpired] = useState(isSessionExpired());
  const [pending, setPending] = useState(0);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [switchWarn, setSwitchWarn] = useState(null);

  useEffect(() => {
    const refresh = () => getPendingCount().then(setPending);
    refresh();
    const off = onSyncState((s) => {
      if (s.phase === "auth_expired") setExpired(true);
      else setExpired(isSessionExpired());
      refresh();
    });
    return off;
  }, []);

  useEffect(() => {
    if (open) setEmail(user?.email || user?.username || "");
  }, [open, user]);

  const doReauth = async (allowOwnerSwitch = false) => {
    setBusy(true);
    try {
      const res = await reauth(email.trim(), password, { allowOwnerSwitch });
      if (res.needsConfirm) {
        setSwitchWarn({ pending: res.pending, name: res.user?.name || res.user?.email });
        return;
      }
      setOpen(false);
      setPassword("");
      setSwitchWarn(null);
      toast.success(pending > 0 ? `Sesión renovada — subiendo ${pending} registro(s) pendientes` : "Sesión renovada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar sesión. Revisá usuario y contraseña.");
    } finally {
      setBusy(false);
    }
  };

  if (!expired) return null;

  return (
    <>
      <div
        data-testid="session-expired-banner"
        role="alert"
        className="slide-down border-b border-destructive/50 bg-destructive/10"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 text-xs font-medium">
          <span className="flex items-center gap-2 text-foreground/90">
            <ShieldAlert size={14} className="text-destructive shrink-0" />
            <span>
              Tu sesión venció. Volvé a iniciar sesión para subir tus{" "}
              <span className="font-semibold" data-testid="session-expired-pending-count">{pending}</span> registros pendientes.
              <span className="text-muted-foreground font-normal"> Tus datos siguen guardados en el dispositivo.</span>
            </span>
          </span>
          <Button
            size="sm"
            data-testid="session-expired-login-button"
            className="h-8 shrink-0"
            onClick={() => setOpen(true)}
          >
            <KeyRound size={13} className="mr-1.5" /> Iniciar sesión
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); setSwitchWarn(null); } }}>
        <DialogContent data-testid="session-reauth-modal" className="sm:max-w-md">
          {!switchWarn ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound size={18} className="text-primary" /> Renovar sesión
                </DialogTitle>
                <DialogDescription>
                  No se cierra la sesión ni se borra nada: solo se renueva el acceso y siguen subiendo tus{" "}
                  <span className="font-semibold text-foreground">{pending}</span> registro(s) pendientes.
                </DialogDescription>
              </DialogHeader>
              <form
                className="space-y-3 py-1"
                onSubmit={(e) => { e.preventDefault(); doReauth(false); }}
              >
                <div className="space-y-1.5">
                  <Label htmlFor="reauth-email" className="text-xs">Email o usuario</Label>
                  <Input
                    id="reauth-email"
                    data-testid="session-reauth-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reauth-password" className="text-xs">Contraseña</Label>
                  <Input
                    id="reauth-password"
                    data-testid="session-reauth-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="h-11"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="session-reauth-submit-button"
                  disabled={busy || !email.trim() || !password}
                  className="w-full h-11"
                >
                  {busy ? "Entrando…" : "Iniciar sesión y seguir subiendo"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <ShieldAlert size={18} /> Esa es otra cuenta
                </DialogTitle>
                <DialogDescription data-testid="session-reauth-switch-warning">
                  Estás entrando con una cuenta distinta ({switchWarn.name}). Si continuás, se borran de este
                  dispositivo los <span className="font-semibold text-foreground">{switchWarn.pending}</span> registro(s)
                  sin subir de la cuenta anterior. Si querés conservarlos, volvé atrás e ingresá con el usuario de siempre.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-col sm:flex-col gap-2">
                <Button
                  variant="outline"
                  data-testid="session-reauth-switch-cancel"
                  className="w-full h-11"
                  onClick={() => { setSwitchWarn(null); setPassword(""); }}
                >
                  Volver e ingresar con mi usuario
                </Button>
                <Button
                  variant="destructive"
                  data-testid="session-reauth-switch-confirm"
                  className="w-full h-10"
                  disabled={busy}
                  onClick={() => doReauth(true)}
                >
                  Entrar con la otra cuenta y perder los pendientes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
